import React, { useState, useEffect } from 'react';
import { Loader2, CheckCircle, XCircle, FileText } from 'lucide-react';
import { ResearchService, type VenueShortlistItem } from '@/services/ResearchService';
import { apiFetch } from '@/lib/api';
import { FileViewerModal, type FileViewerFile } from '@/components/ui/FileViewer/FileViewerModal';

interface CventProposalSidebarProps {
  shortlistId: string;
  venueName?: string;
}

interface ProposalData {
  proposal?: {
    proposalDerivedDetail?: {
      lowestRoomRate?: number;
      maxRoomRate?: number;
      totalSleepRoomCost?: number;
      totalFnbCost?: number;
      totalMiscCost?: number;
    };
    applicableTaxPercent?: number;
    serviceChargePercent?: number;
    isTaxInclusive?: boolean;
    isBreakfastInclusive?: boolean;
    foodBeverageMinimum?: number;
    foodBeverageMinimumNote?: string;
    commissions?: Array<{
      rate?: number;
      applicableType?: string;
    }>;
    meetingSpaceAvailability?: {
      additionalInfo?: string;
      avNeeds?: string;
      meetingRoomReqMetTypeId?: boolean;
    };
    eventAvailability?: {
      startDate?: string;
      endDate?: string;
      availability?: string;
      additionalInfo?: string;
    };
    eventAdditionalInfo?: string;
    taxAdditionalInfo?: string;
    sleepingRoomAvailability?: {
      additionalInfo?: string;
      sleepingRoomReqMetTypeId?: boolean;
    };
    currencyCode?: string;
  };
}

export function CventProposalSidebar({ shortlistId, venueName }: CventProposalSidebarProps) {
  const [loading, setLoading] = useState(true);
  const [shortlistItem, setShortlistItem] = useState<VenueShortlistItem | null>(null);
  const [proposalData, setProposalData] = useState<ProposalData | null>(null);
  const [proposalPdfUrl, setProposalPdfUrl] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<FileViewerFile | null>(null);

  useEffect(() => {
    async function loadData() {
      try {
        setLoading(true);
        const researchService = new ResearchService();
        const item = await researchService.getShortlistItem(shortlistId);
        setShortlistItem(item);

        // Load proposal PDF URL if available
        if (item.cvent_proposal_pdf_attachment_id) {
          try {
            const response = await apiFetch(`/api/attachments/${item.cvent_proposal_pdf_attachment_id}`);
            if (response.ok) {
              const result = await response.json();
              setProposalPdfUrl(result.data?.url || null);
            }
          } catch (err) {
            console.error('Failed to load proposal PDF URL:', err);
          }
        }

        // Extract proposal data from JSONB or use extracted fields
        if (item.cvent_proposal_data_jsonb?.proposal) {
          setProposalData({ proposal: item.cvent_proposal_data_jsonb.proposal });
        } else {
          // Fallback to extracted fields
          const derived: ProposalData['proposal'] = {};
          if (item.cvent_lowest_room_rate !== null || item.cvent_max_room_rate !== null) {
            derived.proposalDerivedDetail = {
              lowestRoomRate: item.cvent_lowest_room_rate ?? undefined,
              maxRoomRate: item.cvent_max_room_rate ?? undefined,
              totalSleepRoomCost: item.cvent_total_sleeping_room_cost ?? undefined,
              totalFnbCost: item.cvent_total_fnb_cost ?? undefined,
              totalMiscCost: item.cvent_total_misc_cost ?? undefined,
            };
          }
          derived.applicableTaxPercent = item.cvent_applicable_tax_percent ?? undefined;
          derived.serviceChargePercent = item.cvent_service_charge_percent ?? undefined;
          derived.isTaxInclusive = item.cvent_is_tax_inclusive ?? undefined;
          derived.isBreakfastInclusive = item.cvent_is_breakfast_inclusive ?? undefined;
          derived.foodBeverageMinimum = item.cvent_food_beverage_minimum ?? undefined;
          derived.commissions = item.cvent_commission_rate
            ? [{ rate: item.cvent_commission_rate }]
            : undefined;
          setProposalData({ proposal: Object.keys(derived).length > 0 ? derived : undefined });
        }
      } catch (error) {
        console.error('Failed to load proposal data:', error);
      } finally {
        setLoading(false);
      }
    }

    if (shortlistId) {
      loadData();
    }
  }, [shortlistId]);

  const formatCurrency = (amount: number | undefined, currencyCode = 'USD') => {
    if (amount === undefined || amount === null) return '—';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateStr: string | undefined) => {
    if (!dateStr) return '—';
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return dateStr;
    }
  };

  const formatBoolean = (value: boolean | undefined | null) => {
    if (value === undefined || value === null) return '—';
    return value ? (
      <span className="flex items-center gap-1 text-success-600 text-xs">
        <CheckCircle className="w-3 h-3" />
        Yes
      </span>
    ) : (
      <span className="flex items-center gap-1 text-gray-500 text-xs">
        <XCircle className="w-3 h-3" />
        No
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
      </div>
    );
  }

  const proposal = proposalData?.proposal;
  const currencyCode = proposal?.currencyCode || shortlistItem?.cvent_currency_code || 'USD';
  const hasData = !!proposal;

  if (!hasData) {
    return (
      <div className="p-4 text-sm text-gray-500">
        <div className="font-medium text-gray-700 mb-2">Cvent Proposal Data</div>
        <div>Not available</div>
      </div>
    );
  }

  const derived = proposal.proposalDerivedDetail;

  return (
    <div className="h-full overflow-y-auto bg-gray-50 border-l border-gray-200">
      <div className="p-4 space-y-6">
        {/* Header */}
        <div className="border-b border-gray-300 pb-3">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-semibold text-gray-900">Cvent Proposal</h3>
              <p className="text-xs text-gray-500 mt-0.5">Pricing & availability details</p>
            </div>
          </div>
          {proposalPdfUrl && (
            <button
              onClick={() => {
                setSelectedFile({
                  url: proposalPdfUrl,
                  name: `${venueName || 'Proposal'} Proposal.pdf`,
                  mimeType: 'application/pdf',
                });
              }}
              className="mt-2 inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-md transition-colors border border-blue-200"
            >
              <FileText className="w-3.5 h-3.5" />
              View Proposal PDF
            </button>
          )}
        </div>

        {/* Room Rates */}
        {derived && (
          <div className="space-y-2">
            <div className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
              Room Rates
            </div>
            <div className="space-y-1.5 text-xs">
              {derived.lowestRoomRate !== undefined && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Lowest Rate:</span>
                  <span className="font-medium text-gray-900">
                    {formatCurrency(derived.lowestRoomRate, currencyCode)}
                  </span>
                </div>
              )}
              {derived.maxRoomRate !== undefined && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Maximum Rate:</span>
                  <span className="font-medium text-gray-900">
                    {formatCurrency(derived.maxRoomRate, currencyCode)}
                  </span>
                </div>
              )}
              {derived.totalSleepRoomCost !== undefined && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Total Room Cost:</span>
                  <span className="font-medium text-gray-900">
                    {formatCurrency(derived.totalSleepRoomCost, currencyCode)}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Tax & Service Charges */}
        {(proposal.applicableTaxPercent !== undefined ||
          proposal.serviceChargePercent !== undefined ||
          proposal.isTaxInclusive !== undefined ||
          proposal.isBreakfastInclusive !== undefined) && (
          <div className="space-y-2">
            <div className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
              Tax & Fees
            </div>
            <div className="space-y-1.5 text-xs">
              {proposal.applicableTaxPercent !== undefined && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Tax:</span>
                  <span className="font-medium text-gray-900">{proposal.applicableTaxPercent}%</span>
                </div>
              )}
              {proposal.serviceChargePercent !== undefined && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Service Charge:</span>
                  <span className="font-medium text-gray-900">
                    {proposal.serviceChargePercent}%
                  </span>
                </div>
              )}
              {proposal.isTaxInclusive !== undefined && (
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Tax Included:</span>
                  {formatBoolean(proposal.isTaxInclusive)}
                </div>
              )}
              {proposal.isBreakfastInclusive !== undefined && (
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Breakfast Included:</span>
                  {formatBoolean(proposal.isBreakfastInclusive)}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Pricing Details */}
        {derived && (derived.totalFnbCost !== undefined || derived.totalMiscCost !== undefined) && (
          <div className="space-y-2">
            <div className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
              Pricing Details
            </div>
            <div className="space-y-1.5 text-xs">
              {derived.totalFnbCost !== undefined && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Total F&B Cost:</span>
                  <span className="font-medium text-gray-900">
                    {formatCurrency(derived.totalFnbCost, currencyCode)}
                  </span>
                </div>
              )}
              {derived.totalMiscCost !== undefined && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Total Misc Cost:</span>
                  <span className="font-medium text-gray-900">
                    {formatCurrency(derived.totalMiscCost, currencyCode)}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Food & Beverage Minimum */}
        {proposal.foodBeverageMinimum !== undefined && proposal.foodBeverageMinimum > 0 && (
          <div className="space-y-2">
            <div className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
              F&B Minimum
            </div>
            <div className="space-y-1.5 text-xs">
              <div className="flex justify-between">
                <span className="text-gray-600">Minimum:</span>
                <span className="font-medium text-gray-900">
                  {formatCurrency(proposal.foodBeverageMinimum, currencyCode)}
                </span>
              </div>
              {proposal.foodBeverageMinimumNote && (
                <div className="text-gray-600 text-xs mt-1 leading-relaxed">
                  {proposal.foodBeverageMinimumNote}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Commission */}
        {proposal.commissions && proposal.commissions.length > 0 && (
          <div className="space-y-2">
            <div className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
              Commission
            </div>
            <div className="space-y-1.5 text-xs">
              {proposal.commissions.map((comm, idx) => (
                <div key={idx} className="flex justify-between">
                  <span className="text-gray-600">
                    {comm.applicableType ? `Rate (${comm.applicableType}):` : 'Rate:'}
                  </span>
                  <span className="font-medium text-gray-900">{comm.rate}%</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Meeting Space Availability */}
        {proposal.meetingSpaceAvailability && (
          <div className="space-y-2">
            <div className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
              Meeting Space
            </div>
            <div className="space-y-1.5 text-xs">
              {proposal.meetingSpaceAvailability.meetingRoomReqMetTypeId !== undefined && (
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Requirements Met:</span>
                  {formatBoolean(proposal.meetingSpaceAvailability.meetingRoomReqMetTypeId)}
                </div>
              )}
              {proposal.meetingSpaceAvailability.avNeeds && (
                <div className="text-gray-600 leading-relaxed">
                  <span className="font-medium">AV Needs:</span>{' '}
                  {proposal.meetingSpaceAvailability.avNeeds}
                </div>
              )}
              {proposal.meetingSpaceAvailability.additionalInfo && (
                <div className="text-gray-600 leading-relaxed mt-1">
                  {proposal.meetingSpaceAvailability.additionalInfo}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Event Availability */}
        {proposal.eventAvailability && (
          <div className="space-y-2">
            <div className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
              Event Availability
            </div>
            <div className="space-y-1.5 text-xs">
              {proposal.eventAvailability.startDate && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Start Date:</span>
                  <span className="font-medium text-gray-900">
                    {formatDate(proposal.eventAvailability.startDate)}
                  </span>
                </div>
              )}
              {proposal.eventAvailability.endDate && (
                <div className="flex justify-between">
                  <span className="text-gray-600">End Date:</span>
                  <span className="font-medium text-gray-900">
                    {formatDate(proposal.eventAvailability.endDate)}
                  </span>
                </div>
              )}
              {proposal.eventAvailability.availability && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Availability:</span>
                  <span className="font-medium text-gray-900">
                    {proposal.eventAvailability.availability}
                  </span>
                </div>
              )}
              {proposal.eventAvailability.additionalInfo && (
                <div className="text-gray-600 leading-relaxed mt-1">
                  {proposal.eventAvailability.additionalInfo}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Event Additional Info */}
        {proposal.eventAdditionalInfo && (
          <div className="space-y-2">
            <div className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
              Event Info
            </div>
            <div className="text-xs text-gray-600 leading-relaxed">
              {proposal.eventAdditionalInfo}
            </div>
          </div>
        )}

        {/* Tax Additional Info */}
        {proposal.taxAdditionalInfo && (
          <div className="space-y-2">
            <div className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
              Tax Details
            </div>
            <div className="text-xs text-gray-600 leading-relaxed">
              {proposal.taxAdditionalInfo}
            </div>
          </div>
        )}

        {/* Room Availability Info */}
        {proposal.sleepingRoomAvailability?.additionalInfo && (
          <div className="space-y-2">
            <div className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
              Room Details
            </div>
            <div className="text-xs text-gray-600 leading-relaxed">
              {proposal.sleepingRoomAvailability.additionalInfo}
            </div>
          </div>
        )}
      </div>

      {/* File Viewer Modal */}
      <FileViewerModal
        isOpen={!!selectedFile}
        onClose={() => setSelectedFile(null)}
        file={selectedFile}
      />
    </div>
  );
}

